'use client';

import { Layout as AntDLayout } from 'antd';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import type { ReactNode } from 'react';

import AppClientProviders from '~/app/(app)/[lng]/components/app-client-providers/app-client-providers';
import useLayout from '~/app/(app)/[lng]/components/layout/use-layout';
import ConfirmRemovingModal from '~/shared/components/confirm-removing-modal/confirm-removing-modal';
import Footer from '~/shared/components/footer';
import Header from '~/shared/components/header';
import { mediaStyles } from '~/shared/components/media-query';
import Modal from '~/shared/components/modal';
import PlatformSidebar from '~/shared/components/platform-sidebar';
import PlatformPageOnboarding from '~/app/(app)/[lng]/components/platform-page-onboarding';
import RouteChangeListener from '~/shared/components/route-change-listener';
import RumInit from '~/shared/components/rum-init';
import { UI_FEATURE_FLAGS } from '~/shared/configs/ui-feature-flags';
import type { Locale } from '~/shared/constants';
import withStorage from '~/shared/hocs/with-storage';
import { ColorSchemeProvider } from '~/shared/hooks/use-color-scheme';
import { useAppSelector } from '~/shared/store/hooks';
import { getIsFullScreenMode, getIsSetFullScreen } from '~/shared/store/slices/lessons/selectors';

import './layout.scss';
import './ui-polish.scss';
import '~/shared/styles/text-selection.scss';
import '~/shared/styles/native-scrollbars.scss';

type LayoutPropsType = Readonly<{
  lng: Locale;
  children: ReactNode;
}>;

const Layout = ({ children, lng }: LayoutPropsType) => {
  const pathname = usePathname();
  const isFullScreenMode = useAppSelector(getIsFullScreenMode);
  const isSetFullScreen = useAppSelector(getIsSetFullScreen);

  const {
    effectiveLng,
    onChangeStateOfModal,
    modal,
    isAdminPanel,
    isClientsPanel,
    stateOfConfirmModal,
    onChangeStateOfConfirmModal,
    t,
    isPlatform,
    shouldShowFooter,
    shouldShowHeader,
    onSignOutCallback,
    menuItems,
    user,
  } = useLayout({ lng });

  const isPlatformSidebarFeatureEnabled = isPlatform && UI_FEATURE_FLAGS.platformSidebar;
  const normalizedPathname = pathname ?? '';
  const isPeoplePage = /\/platform\/(?:managers|teachers|students)(?:\/|$)/.test(normalizedPathname);
  const isLessonImmersiveRoute =
    /\/(call|details)(?:\/|$)/.test(normalizedPathname) || /\/onboarding(?:\/|$)/.test(normalizedPathname);
  const isLessonFullScreen = isFullScreenMode || isSetFullScreen;
  const isPlatformSidebarVisible = isPlatformSidebarFeatureEnabled && !(isLessonImmersiveRoute && isLessonFullScreen);

  // Lesson workspaces own their scrolling. Lock only these routes to the
  // visible viewport so the browser does not add a second right-side scrollbar.
  const isCourseBoardRoute = /\/platform\/(?:online-courses|video-courses)\/[^/]+\/[^/]+\/board(?:\/|$)/.test(
    normalizedPathname,
  );
  const isLessonWorkspaceRoute =
    /\/platform\/(?:online-courses|video-courses)\/[^/]+\/[^/]+\/[^/]+\/(?:call|details|results)(?:\/|$)/.test(
      normalizedPathname,
    );
  const isFixedPlatformViewportRoute = isCourseBoardRoute || isLessonWorkspaceRoute;

  const wrapClassName = classNames('layout__wrap', {
    'layout__wrap--clients-panel': isClientsPanel,
    'layout__wrap--platform': isPlatform,
  });

  const rootClassName = classNames('layout__root', {
    'layout__root--clients-panel': isClientsPanel,
    'layout__root--platform': isPlatform,
    'layout__root--platform-sidebar': isPlatformSidebarVisible,
  });

  const contentClassName = 'layout__clients-panel-content';

  const pageContent = isClientsPanel ? (
    <div className={contentClassName}>{children}</div>
  ) : isPlatform ? (
    <div
      className={classNames('layout__platform-shell', {
        'layout__platform-shell--locked': isFixedPlatformViewportRoute,
      })}
    >
      {isPlatformSidebarVisible && (
        <PlatformSidebar lng={effectiveLng} user={user} isHeaderVisible={shouldShowHeader} />
      )}
      <div
        className={classNames('layout__platform-content', {
          'layout__platform-content--locked': isFixedPlatformViewportRoute,
        })}
      >
        {children}
      </div>
    </div>
  ) : (
    children
  );
  //console.log('Environment:', process.env.NEXT_PUBLIC_DEPLOY_ENV);

  return (
    <ColorSchemeProvider>
      <AppClientProviders lng={effectiveLng}>
        <div className={wrapClassName} data-people-page={isPeoplePage || undefined}>
          <AntDLayout id="root" className={rootClassName}>
            <style type="text/css" dangerouslySetInnerHTML={{ __html: mediaStyles }} />
            <RumInit />
            <RouteChangeListener />
            {isPlatform && <PlatformPageOnboarding />}

            {shouldShowHeader && (
              <Header
                onSignOutCallback={onSignOutCallback}
                isPlatform={isPlatform}
                isAdminPanel={isAdminPanel}
                isClientsPanel={isClientsPanel}
                hidePlatformNavigation={isPlatformSidebarFeatureEnabled}
                lng={effectiveLng}
                menuItems={menuItems}
              />
            )}

            <ConfirmRemovingModal
              title={stateOfConfirmModal?.title}
              isDisabledSubmitButton={stateOfConfirmModal?.isDisabledSubmitButton ?? false}
              isRemoving={stateOfConfirmModal?.isRemoving ?? false}
              message={stateOfConfirmModal?.message}
              wrapClassName={stateOfConfirmModal?.wrapClassName}
              isModalOpen={stateOfConfirmModal?.isShowed ?? false}
              isNoFooter={stateOfConfirmModal?.isNoFooter ?? false}
              isNoTitle={stateOfConfirmModal?.isNoTitle ?? false}
              isFullScreen={stateOfConfirmModal?.isFullScreen ?? false}
              itemKey={stateOfConfirmModal?.itemKey ?? null}
              onTrigger={stateOfConfirmModal?.onTrigger}
              onCancel={stateOfConfirmModal?.onCancel ?? null}
              changeStateOfModal={onChangeStateOfConfirmModal}
            />

            <Modal
              isNoCancelButton
              isNoSubmitButton={modal?.isNoSubmitButton ?? false}
              withNoFooter={modal?.withNoFooter ?? false}
              isDisabledSubmitButton={modal?.isDisabledSubmitButton ?? false}
              withScrollBars
              wrapClassName={modal?.wrapClassName ?? 'layout__modal'}
              isModalOpen={modal?.isShowed ?? false}
              width={600}
              headerTitle={modal?.modalTitle ?? t('modal.details-info.title')}
              changeStateOfModal={(status) =>
                onChangeStateOfModal({
                  ...modal,
                  isShowed: status,
                })
              }
              submitText={t('button.ok')}
            >
              <div className="online-course__modal-content">{modal?.modalContent}</div>
            </Modal>

            {pageContent}

            {shouldShowFooter && <Footer lng={effectiveLng} isAdminPanel={isAdminPanel} isPlatform={isPlatform} />}
          </AntDLayout>
        </div>
      </AppClientProviders>
    </ColorSchemeProvider>
  );
};

export default withStorage(Layout);
